import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { fetchInspModels } from "../services/inspModelCache";
import { getEpochModels } from "../services/epochModelService";
import { t } from "i18next";
import {
  continueTrainInfoState,
  epochResultsState,
  heatmapModelState,
  heatmapModuleMsgState,
  heatmapModuleState,
  heatmapModuleUsersState,
  HeatmapModelRef,
  myAIModuleState,
  trainingStateAtom,
  validListStateAtom,
} from "../recoil/upgrade";
import { useEpochModelModule } from "./useEpochModelModule";
import { projectInfoState, thresholdCustomState } from "../recoil";
import { getInspModel } from "../services/modelService";
import { getLatestInspResult } from "../services/inspLogService";

const parseInfo = (info?: string | null) => {
  try {
    return JSON.parse(info ?? "{}") ?? {};
  } catch {
    return {} as any;
  }
};

// 셀렉트(SelectionInspModel)와 같은 "n회차 Epoch n" 표기. 회차 번호는 임시 모델까지 합쳐 매긴다
const inspLabel = (model: any, roundIds: number[]) => {
  const info = parseInfo(model.info);
  const round = roundIds.indexOf(info?.trainHistory_id) + 1;
  const epoch = info?.epoch?.current;
  return `${round ? `${round}${t("Round")} ` : ""}${t("Epoch")} ${epoch ?? "-"}`;
};

/**
 * 히트맵에 연결된 모델 하나를 관리한다 (ProjectMainPage에서 한 번만 호출).
 * - 히트맵을 켰는데 연결된 모델이 없으면: 가장 최근 저장된 검사모델 → 없으면 학습 중 저장된 최신 epoch 모델
 * - 연결된 모델이 바뀌면 그 모델을 히트맵 spec에 올리고, 검증 결과와 threshold도 그 모델 것으로 바꾼다
 * 히트맵(ImagePart)과 스냅샷(TrainSnapshot)이 이 모듈 하나를 같이 쓴다
 */
export function useHeatmapModel() {
  const { id } = useParams();
  const projectId = Number(id);
  const [heatmapModel, setHeatmapModel] = useRecoilState(heatmapModelState);
  const isTraining = !!useRecoilValue(trainingStateAtom)[projectId];
  const epochResults = useRecoilValue(epochResultsState);
  const setValidList = useSetRecoilState(validListStateAtom);
  const setThresholdCustom = useSetRecoilState(thresholdCustomState);
  const myAIModule = useRecoilValue(myAIModuleState);
  const moduleUsers = useRecoilValue(heatmapModuleUsersState);
  const setHeatmapModule = useSetRecoilState(heatmapModuleState);
  const setHeatmapModuleMsg = useSetRecoilState(heatmapModuleMsgState);
  const isInspProject = useRecoilValue(projectInfoState)?.step === "검사";
  const setContinueTrainInfo = useSetRecoilState(continueTrainInfoState);

  const [noInspModel, setNoInspModel] = useState(false);

  const prevProjectRef = useRef(projectId);
  useEffect(() => {
    if (prevProjectRef.current === projectId) return;
    prevProjectRef.current = projectId;
    setHeatmapModel(null);
    setNoInspModel(false);
  }, [projectId]);

  const latestSavedEpoch = epochResults.filter((r) => r.model_uuid).sort((a, b) => b.epoch - a.epoch)[0];
  const latestSavedUuid = latestSavedEpoch?.model_uuid;

  // 기본 연결: 가장 최근에 저장된 검사모델
  useEffect(() => {
    if (!isInspProject || heatmapModel || !projectId) return;
    let cancelled = false;

    (async () => {
      const [epochRes, models] = await Promise.all([
        getEpochModels({ project_id: projectId }).catch(() => null),
        fetchInspModels().catch(() => []),
      ]);
      if (cancelled) return;
      const mine = models
        .filter((m: any) => parseInfo(m.info)?.project_id === projectId)
        .sort((a: any, b: any) => b.id - a.id);
      setNoInspModel(mine.length === 0);
      if (!mine[0]) return;

      const roundIds = Array.from(
        new Set(
          [...(epochRes?.data?.contents ?? []), ...mine]
            .map((m: any) => parseInfo(m.info)?.trainHistory_id)
            .filter(Boolean),
        ),
      ).sort((a: any, b: any) => a - b) as number[];

      setHeatmapModel({
        uuid: mine[0].uuid,
        label: inspLabel(mine[0], roundIds),
        source: "insp",
        modelId: mine[0].id,
        auto: true,
      });

      const info = parseInfo(mine[0].info);
      setContinueTrainInfo({
        id: mine[0].id,
        uuid: mine[0].uuid,
        trainHistory_id: info?.trainHistory_id ?? null,
        validHistory_id: info?.validHistory_id ?? null,
        epoch: info?.epoch ?? { current: 0, total: 0, num_train: 0 },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [isInspProject, heatmapModel, projectId]);

  // 검사모델이 없을 때만: 학습 중 모델이 저장되면 그걸로 연결
  useEffect(() => {
    if (!isInspProject || heatmapModel || !isTraining) return;
    if (!noInspModel || !latestSavedEpoch) return;
    setHeatmapModel({
      uuid: latestSavedEpoch.model_uuid,
      label: `${t("Epoch")} ${latestSavedEpoch.epoch}`,
      source: "epoch",
      auto: true,
    });
  }, [isInspProject, heatmapModel, isTraining, noInspModel, latestSavedUuid]);

  useEffect(() => {
    if (!heatmapModel || !projectId) return;
    let cancelled = false;

    (async () => {
      const res = await getLatestInspResult(projectId, heatmapModel.uuid).catch(() => null);
      if (cancelled) return;

      if (!res?.results?.length && heatmapModel.auto) return;

      if (heatmapModel.source === "insp" && heatmapModel.modelId != null) {
        const model = await getInspModel(heatmapModel.modelId).catch(() => null);
        const fromOptions = model?.options?.AI_Vision?.labelscore?.para?.threshold_list?.[0];
        if (cancelled) return;
        setThresholdCustom(Number(fromOptions ?? res?.threshold ?? 0));
      } else {
        setThresholdCustom(Number(res?.threshold ?? 0));
      }

      const results = res?.results ?? [];
      setValidList((prev) => {
        const list = prev[projectId] ?? [];
        const labelIdByPath = new Map(list.map((item: any) => [item.path, item.id]));
        return {
          ...prev,
          [projectId]: results.length
            ? results.map((r: any) => ({ ...r, id: labelIdByPath.get(r.path) }))
            : list.map((item: any) => ({ ...item, AILabel: "", score: undefined })),
        };
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [heatmapModel?.uuid, projectId]);

  const { module, msg } = useEpochModelModule({
    modelUuid: heatmapModel?.uuid,
    serverAddress: myAIModule?.serverAddress,
    specId: `${projectId}_scoremap`,
    enabled: isInspProject && moduleUsers > 0 && !!heatmapModel,
  });

  useEffect(() => {
    setHeatmapModule(module);
  }, [module]);

  useEffect(() => {
    setHeatmapModuleMsg(msg);
  }, [msg]);

  return heatmapModel as HeatmapModelRef | null;
}

export function useHeatmapModuleUser(enabled: boolean) {
  const setUsers = useSetRecoilState(heatmapModuleUsersState);

  useEffect(() => {
    if (!enabled) return;
    setUsers((n) => n + 1);
    return () => setUsers((n) => Math.max(0, n - 1));
  }, [enabled]);
}
